import {
    ChatBubbleLeftIcon,
    EnvelopeIcon,
    MapPinIcon,
    PhoneArrowUpRightIcon,
} from "@heroicons/react/24/outline";
import { TouchpointKind } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ButtonSelect } from "components/ui/button-select";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";
import {
    asCadenceUnitInput,
    asTouchpointKinds,
    CadenceUnitInput,
    cadenceUnitOptions,
    parseCadenceCount,
    toggleKind,
} from "./touchpoint-cadence";

type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["tierTouchpoints"]
>[number];

type ChannelOption = {
    id: TouchpointKind;
    label: string;
    icon: React.ComponentType<React.ComponentProps<"svg">>;
};

const channelOptions: ChannelOption[] = [
    { id: "email", label: "Email", icon: EnvelopeIcon },
    { id: "call", label: "Call", icon: PhoneArrowUpRightIcon },
    { id: "text", label: "Text", icon: ChatBubbleLeftIcon },
];

const visitChannelOption: ChannelOption = {
    id: "visit",
    label: "Visit",
    icon: MapPinIcon,
};

interface Props {
    touchpoints: Touchpoint[];
    visitsEnabled: boolean;
    onAdd: () => void;
    onUpdateCount: (id: string, intervalCount: number) => void;
    onUpdateUnit: (id: string, intervalUnit: CadenceUnitInput) => void;
    onUpdateKinds: (id: string, kinds: TouchpointKind[]) => void;
    onDelete: (id: string) => void;
}

export const TierTouchpointsEditor: React.FC<Props> = ({
    touchpoints,
    visitsEnabled,
    onAdd,
    onUpdateCount,
    onUpdateUnit,
    onUpdateKinds,
    onDelete,
}) => {
    const options = visitsEnabled
        ? [...channelOptions, visitChannelOption]
        : channelOptions;
    return (
        <div className="space-y-2 pl-1">
            <div className="text-sm font-medium text-gray-500">Touchpoints</div>
            {touchpoints.length === 0 ? (
                <div className="text-sm text-gray-400">
                    Marketers are not prompted to reach out to this tier until you add a
                    touchpoint.
                </div>
            ) : (
                <div className="space-y-2">
                    {touchpoints.map(touchpoint => {
                        const kinds = asTouchpointKinds(touchpoint.kinds);
                        return (
                            <div
                                key={touchpoint.id}
                                className="flex flex-wrap items-center gap-2">
                                <ButtonSelect
                                    options={options}
                                    selectedIds={kinds}
                                    onToggle={kindId => {
                                        const next = toggleKind(
                                            kinds,
                                            kindId as TouchpointKind,
                                        );
                                        if (next.length === 0) {
                                            return;
                                        }
                                        onUpdateKinds(touchpoint.id, next);
                                    }}
                                />
                                <span className="text-sm text-gray-600">every</span>
                                <div className="w-20">
                                    <AutosavingInput
                                        type="number"
                                        min={1}
                                        initial={String(touchpoint.intervalCount)}
                                        onCommit={value => {
                                            const count = parseCadenceCount(value);
                                            if (count !== null) {
                                                onUpdateCount(touchpoint.id, count);
                                            }
                                        }}
                                    />
                                </div>
                                <div className="w-28">
                                    <Select
                                        textSize="sm"
                                        value={asCadenceUnitInput(
                                            touchpoint.intervalUnit,
                                        )}
                                        onChange={e =>
                                            onUpdateUnit(
                                                touchpoint.id,
                                                asCadenceUnitInput(e.target.value),
                                            )
                                        }>
                                        {cadenceUnitOptions.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <DeleteButton
                                    backgroundColor="bg-white"
                                    size="sm"
                                    onClick={() => onDelete(touchpoint.id)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            <SecondaryButton onClick={onAdd}>Add touchpoint</SecondaryButton>
        </div>
    );
};
